import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ShortCodeEnums, CoreUtils } from '@nhl/nhl-breakaway-ui';

import commonMessages from '~/core/commonMessages';
import { DasherBoardText } from '~/core/enums';
import { getDataLayer } from '~/core/googleAnalyticsUtils';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { GameEventTypeCode, PenaltyTypeCode, ReasonCode } from '../../../../enums';
import { Play as PlayType, PlayByPlayGame, PlayDetails as PlayDetailsType, RosterSpot } from '../../../../types';
import { FormatGoalScorer, FormattedPlayer } from '../../../../utils';
import PPTReplay from '../../../pptReplay/PPTReplay';
import Rink from '../../../rink/Rink';
import { PlayContainer } from '../../styles';
import { RosterMap } from '../../types';
import { EXPANDABLE_EVENTS } from '../../utils';
import EventMarker from '../eventMarker/EventMarker';
import Goal from '../goal/Goal';
import { GoalTeam } from '../goal/components/GoalTeam';

import {
    ExpandColumn,
    LogoPlaceholder,
    NestedPlayDetail,
    PeriodContainer,
    PlayColumn,
    PlayDetails,
    PlayHeader,
    SecondaryAssist,
    TimeColumn,
    TimeContainer,
} from './styles';

const VOWELS = ['A', 'E', 'I', 'O', 'U'];

type PlayersForPlay = {
    primaryPlayer: ReactNode;
    secondaryPlayer: ReactNode;
    tertiaryPlayer: ReactNode;
    primaryPlayerDetails?: RosterSpot;
    secondaryPlayerDetails?: RosterSpot;
    tertiaryPlayerDetails?: RosterSpot;
};

type Props = {
    rosterMap: RosterMap;
    play: PlayType;
    game: PlayByPlayGame;
};

const Play: FC<Props> = ({ game, rosterMap, play }) => {
    const intl = useIntl();
    const theme = useTheme();

    const hasPptUrl = play.pptReplayUrl !== undefined;
    const details = play?.details;
    const isGoalEvent = play.typeCode === GameEventTypeCode.GOAL;
    const isShootoutEvent = play?.periodDescriptor.periodType === ShortCodeEnums.PeriodType.shootout;
    const canExpand = EXPANDABLE_EVENTS.includes(play.typeCode) && (details?.xCoord !== undefined || hasPptUrl);
    const scoringTeam = details?.eventOwnerTeamId === game.awayTeam.id ? game.awayTeam : game.homeTeam;
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggleClick = () => {
        // if the play isn't currently expanded, and we're about to show it we fire a GA event
        if (!isExpanded) {
            getDataLayer().push({
                event: 'play_open_click',
                game_id: game.id,
                play_type: play.typeDescKey,
                game_state: game.gameState,
            });
        }

        setIsExpanded(!isExpanded);
    };

    const createPlayers = useCallback(
        (details: PlayDetailsType | undefined, idName1: string, idName2 = '', idName3 = ''): PlayersForPlay => {
            const playerId1 = details?.[idName1];
            const playerId2 = details?.[idName2];
            const playerId3 = details?.[idName3];

            const primaryPlayerDetails = rosterMap[playerId1];
            const secondaryPlayerDetails = rosterMap[playerId2];
            const tertiaryPlayerDetails = rosterMap[playerId3];

            const primaryPlayer =
                playerId1 && rosterMap[playerId1] ? <FormattedPlayer player={primaryPlayerDetails} /> : null;
            const secondaryPlayer =
                playerId2 && rosterMap[playerId2] ? <FormattedPlayer player={secondaryPlayerDetails} /> : null;
            const tertiaryPlayer =
                playerId3 && rosterMap[playerId3] ? <FormattedPlayer player={tertiaryPlayerDetails} /> : null;

            return {
                primaryPlayer,
                secondaryPlayer,
                tertiaryPlayer,
                primaryPlayerDetails,
                secondaryPlayerDetails,
                tertiaryPlayerDetails,
            };
        },
        [rosterMap]
    );

    const header: ReactNode = useMemo(() => {
        if (isGoalEvent) {
            const goalScorer = createPlayers(details, 'scoringPlayerId')?.primaryPlayer;

            return (
                <FormatGoalScorer
                    goalScorer={goalScorer}
                    isShootoutEvent={isShootoutEvent}
                    totalGoals={details?.scoringPlayerTotal}
                />
            );
        }
        return <CoreUtils.SafeFormattedMessage message={commonMessages.gameEventTypeDescriptions[play.typeDescKey]} />;
    }, [createPlayers, details, isGoalEvent, play]);

    const description: ReactNode = useMemo(() => {
        switch (play.typeCode) {
            case GameEventTypeCode.FACEOFF: {
                const { primaryPlayer, secondaryPlayer } = createPlayers(details, 'winningPlayerId', 'losingPlayerId');
                return primaryPlayer && secondaryPlayer ? (
                    <FormattedMessage
                        description="A message describing which player won the face-off. Ex. John Doe #10 face-off won against Mike Smith #20"
                        defaultMessage="{primaryPlayer} face-off won against {secondaryPlayer}"
                        values={{ primaryPlayer, secondaryPlayer }}
                    />
                ) : null;
            }
            case GameEventTypeCode.HIT: {
                const { primaryPlayer, secondaryPlayer } = createPlayers(details, 'hittingPlayerId', 'hitteePlayerId');
                return primaryPlayer && secondaryPlayer ? (
                    <FormattedMessage
                        description="A message describing that a player hit another player. Ex. John Doe #10 hit Mike Smith #20"
                        defaultMessage="{primaryPlayer} hit {secondaryPlayer}"
                        values={{ primaryPlayer, secondaryPlayer }}
                    />
                ) : null;
            }
            case GameEventTypeCode.GIVEAWAY: {
                const { primaryPlayer } = createPlayers(details, 'playerId');
                return primaryPlayer ? (
                    <FormattedMessage
                        description="A message describing that a player gaveaway the puck. Ex. Giveaway by John Doe #10"
                        defaultMessage="Giveaway by {primaryPlayer}"
                        values={{ primaryPlayer }}
                    />
                ) : null;
            }
            case GameEventTypeCode.TAKEAWAY: {
                const { primaryPlayer } = createPlayers(details, 'playerId');
                return primaryPlayer ? (
                    <FormattedMessage
                        description="A message describing that a player took away the puck. Ex. Takeaway by John Doe #10"
                        defaultMessage="Takeaway by {primaryPlayer}"
                        values={{ primaryPlayer }}
                    />
                ) : null;
            }
            case GameEventTypeCode.GOAL: {
                if (isShootoutEvent) {
                    return null;
                }
                const { primaryPlayer, secondaryPlayer } = createPlayers(details, 'assist1PlayerId', 'assist2PlayerId');
                return primaryPlayer ? (
                    <FormattedMessage
                        description="A message describing the players who assisted a goal. Ex. Assists: John Doe #10 (4), Mike Smith #20 (11)"
                        defaultMessage="Assists: {primaryPlayer} ({primaryPlayerTotal})<secondaryAssist>, {secondaryPlayer} ({secondaryPlayerTotal})</secondaryAssist>"
                        values={{
                            primaryPlayer,
                            secondaryPlayer,
                            primaryPlayerTotal: details?.assist1PlayerTotal,
                            secondaryPlayerTotal: details?.assist2PlayerTotal,
                            secondaryAssist: (text) => (
                                <SecondaryAssist $showAssist={secondaryPlayer !== null}>{text}</SecondaryAssist>
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage {...commonMessages.statsDescriptions.long.unassisted} />
                );
            }
            case GameEventTypeCode.SHOT_ON_GOAL: {
                const { primaryPlayer, secondaryPlayer } = createPlayers(details, 'shootingPlayerId', 'goalieInNetId');
                const shotTypeMessage = details?.shotType && commonMessages.eventDescriptions[details.shotType];
                const shotType = shotTypeMessage ? (
                    <NestedPlayDetail $forceLowercase={intl.locale !== ShortCodeEnums.Language.German}>
                        <CoreUtils.SafeFormattedMessage message={shotTypeMessage} />
                    </NestedPlayDetail>
                ) : (
                    ''
                );
                return primaryPlayer && secondaryPlayer ? (
                    <FormattedMessage
                        description="A message describing a shot that was stopped by the goaltender. Ex. John Doe #10 wrist shot saved by Mike Smith #20"
                        defaultMessage="{primaryPlayer} {shotType} shot saved by {secondaryPlayer}"
                        values={{ primaryPlayer, secondaryPlayer, shotType }}
                    />
                ) : (
                    primaryPlayer
                );
            }
            case GameEventTypeCode.MISSED_SHOT: {
                const { primaryPlayer } = createPlayers(details, 'shootingPlayerId');
                const shotTypeMessage = details?.shotType && commonMessages.eventDescriptions[details.shotType];
                const shotType = shotTypeMessage ? (
                    <NestedPlayDetail $forceLowercase={intl.locale !== ShortCodeEnums.Language.German}>
                        <CoreUtils.SafeFormattedMessage message={shotTypeMessage} />
                    </NestedPlayDetail>
                ) : (
                    ''
                );
                const shotLocationMessage = details?.reason && commonMessages.missedShotDescriptions[details?.reason];
                const shotLocation = shotLocationMessage ? (
                    <NestedPlayDetail $forceLowercase={intl.locale !== ShortCodeEnums.Language.German}>
                        <CoreUtils.SafeFormattedMessage message={shotLocationMessage} />
                    </NestedPlayDetail>
                ) : (
                    ''
                );
                return primaryPlayer ? (
                    <FormattedMessage
                        description="A message describing a shot was attempted but it was not on net. Ex. John Doe #10 wrist shot over the net"
                        defaultMessage="{primaryPlayer} {shotType} shot {shotLocation}"
                        values={{ primaryPlayer, shotType, shotLocation }}
                    />
                ) : null;
            }
            case GameEventTypeCode.BLOCKED_SHOT: {
                const { primaryPlayer, primaryPlayerDetails, secondaryPlayer } = createPlayers(
                    details,
                    'shootingPlayerId',
                    'blockingPlayerId'
                );

                const shootingPlayerName = parseValueFromLocalizedAttribute(primaryPlayerDetails?.firstName);
                const isVowel = VOWELS.includes(shootingPlayerName[0]);

                if (details?.reason === ReasonCode.TEAMMATE_BLOCKED) {
                    return (
                        <FormattedMessage
                            description="A message describing that a players shot was blocked by a teammate. Ex. John Doe #10 shot blocked by teammate"
                            defaultMessage="{primaryPlayer} shot blocked by teammate"
                            values={{ primaryPlayer, isVowel }}
                        />
                    );
                } else if (details?.reason === ReasonCode.DEFLECTED || details?.reason === ReasonCode.OTHER_BLOCK) {
                    return (
                        <FormattedMessage
                            description="A message describing that a players shot was blocked. Ex. John Doe #10 shot blocked"
                            defaultMessage="{primaryPlayer} shot blocked"
                            values={{ primaryPlayer, isVowel }}
                        />
                    );
                }

                return primaryPlayer && secondaryPlayer ? (
                    <FormattedMessage
                        description="A message describing that a players shot was blocked. Ex. John Doe #10 shot blocked by Mike Smith #20"
                        defaultMessage="{primaryPlayer} shot blocked by {secondaryPlayer}"
                        values={{ primaryPlayer, secondaryPlayer, isVowel }}
                    />
                ) : null;
            }
            case GameEventTypeCode.PENALTY: {
                const time = details?.duration && (
                    <FormattedNumber value={details?.duration} style="unit" unit="minute" unitDisplay="long" />
                );
                const penaltyMessage = details?.descKey && commonMessages.penaltyDetailDescriptions[details.descKey];
                const penalty = penaltyMessage && (
                    <NestedPlayDetail>
                        <CoreUtils.SafeFormattedMessage message={penaltyMessage} />
                    </NestedPlayDetail>
                );
                const { primaryPlayer, secondaryPlayer, tertiaryPlayer } = createPlayers(
                    details,
                    'committedByPlayerId',
                    'drawnByPlayerId',
                    'servedByPlayerId'
                );

                if (details?.typeCode === PenaltyTypeCode.PENALTY_SHOT) {
                    return (
                        <FormattedMessage
                            description="A message describing a penalty shot in the game. Ex. John Doe #10 Penalty Shot - Hooking on breakaway drawn by Mike Smith #20"
                            defaultMessage="{primaryPlayer} {penalty}<drawnBy> drawn by {secondaryPlayer}</drawnBy>"
                            values={{
                                primaryPlayer,
                                secondaryPlayer,
                                penalty,
                                drawnBy: (text) => secondaryPlayer && <NestedPlayDetail>{text}</NestedPlayDetail>,
                            }}
                        />
                    );
                }

                return (
                    <FormattedMessage
                        description="A message describing a penalty in the game. Ex. John Doe #10 2 minutes for tripping Mike Smith #20 served by Wayne Gretzky #99"
                        defaultMessage="<committedBy>{primaryPlayer} </committedBy><timeText>{time} for </timeText>{penalty}<drawnBy> {secondaryPlayer}</drawnBy><servedBy> served by {tertiaryPlayer}</servedBy>"
                        values={{
                            primaryPlayer,
                            secondaryPlayer,
                            tertiaryPlayer,
                            penalty,
                            time,
                            timeText: (text) => time && <NestedPlayDetail>{text}</NestedPlayDetail>,
                            committedBy: (text) => primaryPlayer && <NestedPlayDetail>{text}</NestedPlayDetail>,
                            drawnBy: (text) => secondaryPlayer && <NestedPlayDetail>{text}</NestedPlayDetail>,
                            servedBy: (text) => tertiaryPlayer && <NestedPlayDetail>{text}</NestedPlayDetail>,
                        }}
                    />
                );
            }
            // Shootout or Penalty Shot
            case GameEventTypeCode.FAILED_SHOT_ATTEMPT: {
                return createPlayers(details, 'shootingPlayerId').primaryPlayer;
            }
            default:
                return null;
        }
    }, [createPlayers, details, intl.locale, play]);

    let showTimeColumn = true;

    if (game.shootoutInUse && game.maxPeriods === play.periodDescriptor.number) {
        showTimeColumn = false;
    }

    const teamDisplay = (): ReactNode => {
        if (game.homeTeam.id === details?.eventOwnerTeamId) {
            const backgroundColor =
                isGoalEvent && theme.Team[game.homeTeam.abbrev]
                    ? theme.Team[game.homeTeam.abbrev].primary
                    : theme.Surface.primary;

            return <GoalTeam team={game.homeTeam} backgroundColor={backgroundColor} />;
        } else if (game.awayTeam.id === details?.eventOwnerTeamId) {
            const backgroundColor =
                isGoalEvent && theme.Team[game.awayTeam.abbrev]
                    ? theme.Team[game.awayTeam.abbrev].primary
                    : theme.Surface.primary;

            return <GoalTeam team={game.awayTeam} backgroundColor={backgroundColor} />;
        }
        return <LogoPlaceholder />;
    };

    return (
        <>
            {isGoalEvent && <Goal game={game} play={play} scoringTeam={scoringTeam} />}
            <PlayContainer $isGoalEvent={isGoalEvent} $teamAbbrev={scoringTeam.abbrev}>
                <TimeColumn $isGoalEvent={isGoalEvent} $teamAbbrev={scoringTeam.abbrev}>
                    {showTimeColumn && <TimeContainer>{play.timeRemaining}</TimeContainer>}
                    <PeriodContainer>
                        {CoreUtils.getPeriodDisplayFromDescriptor(intl, play.periodDescriptor, game.gameType)}
                    </PeriodContainer>
                </TimeColumn>
                {teamDisplay()}
                <PlayColumn $isGoalEvent={isGoalEvent} $teamAbbrev={scoringTeam.abbrev}>
                    <PlayHeader>{header}</PlayHeader>
                    <PlayDetails>{description}</PlayDetails>
                </PlayColumn>
                {canExpand && (
                    <ExpandColumn>
                        {isExpanded ? (
                            <ExpandLessIcon onClick={handleToggleClick} />
                        ) : (
                            <ExpandMoreIcon onClick={handleToggleClick} />
                        )}
                    </ExpandColumn>
                )}
            </PlayContainer>
            {isExpanded &&
                (play.pptReplayUrl ? (
                    <PPTReplay
                        gameId={game.id}
                        gameState={game.gameState}
                        homeTeam={game.homeTeam}
                        awayTeam={game.awayTeam}
                        homeTeamDefendingSide={play.homeTeamDefendingSide}
                        pptReplayUrl={play.pptReplayUrl}
                        goalScorerId={play.details?.playerId}
                        dasherBoardText={DasherBoardText.GOAL}
                        period={play.periodDescriptor.number}
                        eventId={play.eventId}
                    />
                ) : (
                    <Rink
                        gameId={game.id}
                        homeTeam={game.homeTeam}
                        awayTeam={game.awayTeam}
                        homeTeamDefendingSide={play.homeTeamDefendingSide}
                        period={play.periodDescriptor.number}
                        eventId={play.eventId.toString()}
                    >
                        <EventMarker
                            play={play}
                            game={game}
                            team={details?.eventOwnerTeamId === game.awayTeam.id ? game.awayTeam : game.homeTeam}
                        />
                    </Rink>
                ))}
        </>
    );
};

export default Play;
